import { Dispatch } from 'redux';

import { get, RequestExceptionWithResponse } from '@eika-infrastruktur/request';

import CustomAppTypes from '../config/customAppTypes';
import { AppState } from '../reducers';
import { ContextReducer } from '../reducers/context';
import parseUrlParams from '../util/urlParams';
import { fetchCards } from './cards';
import { fetchStatus, FetchStatusResponse } from './checkStatus';
import { fetchConsent } from './consent';
import { featureToggle } from './feature';
import { fetchCase } from './fetchCase';
import { fetchNafCache } from './naf';
import { getOrCreateSblConsent } from './sbl';
import { getRequestHeaders } from './tools';
import { actionTypes, AppActions } from './types';
import { fetchParams } from './urlParams';

type Action<T> = import('./types').Action<T>;
type FetchUserSessionResponse = import('../generated-types').Session;
type FetchUserContextResponse = import('../generated-types').Kundeinfo;

export interface FetchUserSessionStartedAction extends Action<typeof actionTypes.FETCH_USER_SESSION_STARTED> {
    type: typeof actionTypes.FETCH_USER_SESSION_STARTED;
}

export interface FetchUserSessionFulfilledAction extends Action<typeof actionTypes.FETCH_USER_SESSION_FULFILLED> {
    type: typeof actionTypes.FETCH_USER_SESSION_FULFILLED;
    payload: FetchUserSessionResponse;
}

export interface FetchUserSessionFailedAction extends Action<typeof actionTypes.FETCH_USER_SESSION_FAILED> {
    type: typeof actionTypes.FETCH_USER_SESSION_FAILED;
    payload: RequestExceptionWithResponse;
}

export interface FetchUserContextStartedAction extends Action<typeof actionTypes.FETCH_USER_CONTEXT_STARTED> {
    type: typeof actionTypes.FETCH_USER_CONTEXT_STARTED;
}

export interface FetchUserContextFulfilledAction extends Action<typeof actionTypes.FETCH_USER_CONTEXT_FULFILLED> {
    type: typeof actionTypes.FETCH_USER_CONTEXT_FULFILLED;
    payload: FetchUserContextResponse;
}

export interface FetchUserContextFailedAction extends Action<typeof actionTypes.FETCH_USER_CONTEXT_FAILED> {
    type: typeof actionTypes.FETCH_USER_CONTEXT_FAILED;
    payload: RequestExceptionWithResponse;
}

export interface FetchTestUserToggleFulfilledAction
    extends Action<typeof actionTypes.FETCH_TEST_USER_TOGGLE_FULFILLED> {
    type: typeof actionTypes.FETCH_TEST_USER_TOGGLE_FULFILLED;
    payload: boolean;
}

export type UserActions =
    | FetchUserSessionStartedAction
    | FetchUserSessionFulfilledAction
    | FetchUserSessionFailedAction
    | FetchUserContextStartedAction
    | FetchUserContextFulfilledAction
    | FetchUserContextFailedAction
    | FetchTestUserToggleFulfilledAction;

export const fetchUserSessionStarted = (): FetchUserSessionStartedAction => ({
    type: actionTypes.FETCH_USER_SESSION_STARTED,
});
export const fetchUserSessionFulfilled = (json: FetchUserSessionResponse): FetchUserSessionFulfilledAction => ({
    type: actionTypes.FETCH_USER_SESSION_FULFILLED,
    payload: json,
});
export const fetchUserSessionFailed = (error: RequestExceptionWithResponse): FetchUserSessionFailedAction => ({
    type: actionTypes.FETCH_USER_SESSION_FAILED,
    payload: error,
});

export const fetchUserContextStarted = (): FetchUserContextStartedAction => ({
    type: actionTypes.FETCH_USER_CONTEXT_STARTED,
});
export const fetchUserContextFulfilled = (json: FetchUserContextResponse): FetchUserContextFulfilledAction => ({
    type: actionTypes.FETCH_USER_CONTEXT_FULFILLED,
    payload: json,
});
export const fetchUserContextFailed = (error: RequestExceptionWithResponse): FetchUserContextFailedAction => ({
    type: actionTypes.FETCH_USER_CONTEXT_FAILED,
    payload: error,
});
export const fetchTestUserToggleFulfilled = (json: boolean): FetchTestUserToggleFulfilledAction => ({
    type: actionTypes.FETCH_TEST_USER_TOGGLE_FULFILLED,
    payload: json,
});

export const noCardsAndNoOngoing = (context: ContextReducer, status: FetchStatusResponse | null, hasCards: boolean) => {
    const routeParams = parseUrlParams();
    const hasValidParams = ['gold', 'business'].includes(routeParams.type) && !Number.isNaN(routeParams.endre);
    return (
        (CustomAppTypes.isNaf(context) || CustomAppTypes.isLokalbank(context)) &&
        !hasCards &&
        !hasValidParams &&
        status?.status !== 'SOKNAD_PAAGAAENDE'
    );
};

/* User Context */
export const fetchUserContext =
    () =>
    async (dispatch: Dispatch<AppActions>, getState: () => AppState): Promise<void> => {
        const { loader, context, naf, lbs } = getState();
        const { hasFetchedUserContext, isFetchingUserContext } = loader;
        if (!hasFetchedUserContext && !isFetchingUserContext) {
            try {
                dispatch(fetchUserContextStarted());
                const response: FetchUserContextResponse = await get(
                    `${actionTypes.RESOURCE_PATH}/kunde`,
                    getRequestHeaders(context.config.bankregnr),
                );
                dispatch(fetchUserContextFulfilled(response));

                await getOrCreateSblConsent(dispatch, getState);

                const { isValidCustomer } = getState().context;
                if (isValidCustomer) {
                    /* Fetch NAF Cache */
                    if (CustomAppTypes.isNaf(context) && !loader.hasFetchedNafCache) {
                        await fetchNafCache(dispatch, getState);
                    } else if (!CustomAppTypes.isNaf(context) || naf.isMember) {
                        const cards = await fetchCards(dispatch, getState);
                        /* Fetch Params */
                        await fetchParams(dispatch, getState);

                        const routeParams = parseUrlParams();
                        if (routeParams?.id) {
                            await fetchCase(dispatch, getState, routeParams.id);
                        } else {
                            const status = await fetchStatus(dispatch, getState);
                            const { type } = routeParams;
                            if (
                                noCardsAndNoOngoing(
                                    context,
                                    status,
                                    !!(cards?.eksisterendeKort && cards?.eksisterendeKort?.length > 0),
                                )
                            ) {
                                const lbsSuffix = lbs.lbsBankregnr !== null ? `&lbsbnr=${lbs.lbsBankregnr}` : '';
                                window.location.href = `?type=gold&endre=0${lbsSuffix}`;
                            }

                            if (CustomAppTypes.isAgri(context) && ['private', 'employee'].includes(type)) {
                                window.location.href = `?agricard=${routeParams.agricard}&type=${
                                    type === 'private' ? 'gold' : 'business'
                                }&endre=0`;
                            }

                            /* Fetch Consent */
                            await fetchConsent(dispatch, getState);
                        }
                    }
                }
            } catch (error) {
                dispatch(fetchUserContextFailed(error));
            }
        }
    };

export const fetchUserSession =
    () =>
    async (dispatch: Dispatch<AppActions>, getState: () => AppState): Promise<void> => {
        const { loader, context, lbs } = getState();
        const { hasFetchedUserSession, isFetchingUserSession } = loader;
        if (
            !hasFetchedUserSession &&
            !isFetchingUserSession &&
            (!CustomAppTypes.isLbs(context) || lbs.lbsBankregnr !== null)
        ) {
            try {
                dispatch(fetchUserSessionStarted());
                const response: FetchUserSessionResponse = await get(
                    `${actionTypes.RESOURCE_PATH}/kunde/sesjon`,
                    getRequestHeaders(context.config.bankregnr),
                );

                await featureToggle('kredittkort-traffic-light', dispatch, context.config.bankregnr);

                await featureToggle('kredittkort-block-eika-new-card', dispatch, context.config.bankregnr);

                await featureToggle('kredittkort-sbl-kyc-blocked-disabled', dispatch, context.config.bankregnr);

                dispatch(fetchUserSessionFulfilled(response));
                // @ts-ignore
                if (response.valid) await dispatch(fetchUserContext());
            } catch (error) {
                dispatch(fetchUserSessionFailed(error));
            }
        }
    };

export const fetchTestUserToggle =
    () =>
    async (dispatch: Dispatch<UserActions>, getState: () => AppState): Promise<void> => {
        const response: boolean = await get(
            `${actionTypes.RESOURCE_PATH}/features/kredittbank-kort-test-user`,
            getRequestHeaders(getState().context.config.bankregnr),
        );
        dispatch(fetchTestUserToggleFulfilled(response));
    };
